import axios from 'axios';
import pako from 'pako';
import {
  addToQueue,
  isOnline,
  isOfflineError,
  removeFromQueue,
  getQueue,
  saveQueue,
} from './offlineQueue';
import getApiBaseUrl from '../getApiBaseUrl';

/**
 * Make the actual HTTP request
 */
const makeRequest = async ({ method, path, data = {}, config = {}, compress = false }) => {
  const baseURL = getApiBaseUrl();
  if (!baseURL) {
    const err = new Error(
      'REACT_APP_API_BASE_URL (or REACT_APP_API_URL) is not set. Configure it in your build environment.'
    );
    // eslint-disable-next-line no-console
    console.error('[axiosWrapper]', err.message);
    return { data: null, error: err };
  }
  const url = `${baseURL}${path}`;

  let finalData = data;
  // Start with headers from config, if any
  const headers = { ...(config.headers || {}) };

  // Get token from localStorage and add to Authorization header
  // This must be set after config.headers to ensure it's not overridden
  const token = localStorage.getItem('authToken');
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (compress && data && typeof data === 'object') {
    try {
      const jsonString = JSON.stringify(data);
      const gzipData = pako.gzip(jsonString); // Compress the JSON data

      finalData = new Blob([gzipData], { type: 'application/gzip' });

      headers['Content-Encoding'] = 'gzip';
      headers['Content-Type'] = 'application/json';
    } catch (compressionError) {
      return { data: null, error: compressionError };
    }
  }

  // Extract config properties excluding headers (we handle headers separately)
  const { headers: _configHeaders, ...restConfig } = config;

  try {
    const response = await axios({
      method,
      url,
      data: finalData,
      withCredentials: true,
      headers,
      ...restConfig, // Spread rest of config (like responseType) but not headers
    });
    return { data: response.data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * Process a queued request
 */
const processQueuedRequest = async (queuedRequest) => {
  const { method, path, data, config, compress, id } = queuedRequest;

  try {
    const result = await makeRequest({ method, path, data, config, compress });

    if (!result.error) {
      // Success - remove from queue
      removeFromQueue(id);
      return { success: true, result };
    }
    if (isOfflineError(result.error)) {
      // Still offline - keep in queue but increment retry count
      const queue = getQueue();
      const request = queue.find((req) => req.id === id);
      if (request) {
        request.retryCount = (request.retryCount || 0) + 1;
        saveQueue(queue);
      }
      return { success: false, result };
    }
    // Other error (not network-related) - remove from queue
    removeFromQueue(id);
    return { success: false, result };
  } catch (error) {
    // Error processing - keep in queue
    return { success: false, result: { data: null, error } };
  }
};

/**
 * Main axios wrapper with offline support and optimistic updates
 *
 * All requests are optimistic by default (optimistic = true).
 * This means:
 * - When offline: Returns mock success response immediately, queues request for later
 * - When online but network fails: Returns mock success response, queues request for retry
 * - Redux updates can happen immediately without waiting for API response
 *
 * To disable optimistic updates, explicitly set optimistic: false
 *
 * @param {string} method - HTTP method (get, post, put, delete, etc.)
 * @param {string} path - API endpoint path
 * @param {object} data - Request body data
 * @param {object} config - Additional axios config
 * @param {boolean} compress - Whether to compress request data
 * @param {boolean} optimistic - Enable optimistic updates (default: true)
 */
const axiosWrapper = async ({
  method,
  path,
  data = {},
  config = {},
  compress = false,
  optimistic = true,
}) => {
  // Enforce optimistic updates by default - only disable if explicitly set to false
  const isOptimistic = optimistic !== false;

  // If we're offline or the request is a GET (which we don't queue), try the request
  // GET requests should fail gracefully if offline
  if (!isOnline() && method.toLowerCase() !== 'get') {
    // Queue the request for later
    const queuedRequest = {
      method,
      path,
      data,
      config,
      compress,
      optimistic: isOptimistic,
    };

    addToQueue(queuedRequest);

    // For optimistic updates, return a mock success response
    // This allows components to proceed with Redux updates immediately
    // The actual request will be processed when back online
    if (isOptimistic) {
      // Generate a mock response structure that components expect
      // Components typically check response.data?.data, so we provide that structure
      const mockResponse = {
        data: data || {},
        queued: true,
        optimistic: true,
      };

      // For POST requests, add a mock ID if creating a resource
      if (method.toLowerCase() === 'post' && data) {
        mockResponse.data = {
          ...data,
          _id: data._id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          id: data.id || mockResponse.data._id,
          createdAt: data.createdAt || new Date().toISOString(),
          updatedAt: data.updatedAt || new Date().toISOString(),
        };
      }

      return {
        data: { data: mockResponse.data },
        error: null,
        queued: true,
        optimistic: true,
        message: 'Request queued for when connection is restored',
      };
    }
    return {
      data: null,
      error: {
        message: 'Offline - request queued',
        code: 'OFFLINE',
        queued: true,
      },
    };
  }

  // Try to make the request
  const result = await makeRequest({ method, path, data, config, compress });

  // If request failed due to network/offline error, queue it
  if (result.error && isOfflineError(result.error) && method.toLowerCase() !== 'get') {
    const queuedRequest = {
      method,
      path,
      data,
      config,
      compress,
      optimistic: isOptimistic,
    };

    addToQueue(queuedRequest);

    // For optimistic updates, return a mock success response
    // This allows components to proceed with Redux updates immediately
    if (isOptimistic) {
      // Generate a mock response structure that components expect
      const mockResponse = {
        data: data || {},
        queued: true,
        optimistic: true,
      };

      // For POST requests, add a mock ID if creating a resource
      if (method.toLowerCase() === 'post' && data) {
        mockResponse.data = {
          ...data,
          _id: data._id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          id: data.id || mockResponse.data._id,
          createdAt: data.createdAt || new Date().toISOString(),
          updatedAt: data.updatedAt || new Date().toISOString(),
        };
      }

      return {
        data: { data: mockResponse.data },
        error: null,
        queued: true,
        optimistic: true,
        message: 'Request queued for when connection is restored',
      };
    }
    return {
      ...result,
      queued: true,
    };
  }

  // Request succeeded or failed with non-network error
  return result;
};

/**
 * Process all queued requests (called when back online)
 */
axiosWrapper.processQueue = async () => {
  const queue = getQueue();

  if (queue.length === 0) {
    return { processed: 0, succeeded: 0, failed: 0 };
  }

  console.log(`[axiosWrapper] Processing ${queue.length} queued requests...`);

  let processed = 0;
  let succeeded = 0;
  let failed = 0;

  // Process requests sequentially to avoid overwhelming the server
  // Using for loop with index to satisfy linter (no for...of with await)
  for (let i = 0; i < queue.length; i += 1) {
    if (!isOnline()) {
      console.log('[axiosWrapper] Connection lost while processing queue');
      break;
    }

    // eslint-disable-next-line no-await-in-loop
    const result = await processQueuedRequest(queue[i]);
    processed += 1;

    if (result.success) {
      succeeded += 1;
    } else {
      failed += 1;
    }

    // Small delay between requests to avoid rate limiting
    if (i < queue.length - 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        setTimeout(resolve, 100);
      });
    }
  }

  console.log(`[axiosWrapper] Queue processing complete: ${succeeded} succeeded, ${failed} failed`);

  return { processed, succeeded, failed };
};

export default axiosWrapper;
